'use client';

import { useCppConfig, useCppRoutes, useRewardsHubConfig } from '@/hooks/use-rewards/use-rewards';
import type { RewardsPageConfig } from '@/logic/page-config/rewards/rewards-page-config';
import { isIosWrapper } from '@/logic/platform';
import { RewardsAppProvider } from '@fanduel/rewards-hub-client/provider';
import { PromotionDetailsPage } from '@fanduel/rewards-hub-client/promotion-details';
import { GameZone } from '@/app/components/game-zone/game-zone';
import { REFERRER_TYPE_REWARDS, useCppEventsHandler } from '@/hooks/use-rewards/use-cpp-events-handler';
import { useEffect, useMemo, useRef, useState, type ComponentProps } from 'react';
import { ddRumTimingOnce } from '@/utils/datadog';
import { PROMO_DETAILS_TIMINGS } from '@/utils/datadog/rum-timings';
import { useRenderTiming } from '@/hooks/use-render-timing/use-render-timing';
import { PokerBuildPlaytechGate } from '@/app/components/playtech-gate/poker-build-playtech-gate';
import {
    trackRewardsHubPromoEventToAmplitude,
    type RewardsHubPromoEvent
} from '@/tracking/amplitude/events/rewards-hub/promo-events';
import { REWARDS_MODULE } from '@/tracking/amplitude/events/rewards-hub/event-names';
import { useRewardsPromoTracking } from '@/hooks/use-rewards/use-rewards-promo-tracking';
import type { RewardsHubSharedState } from '@/tracking/amplitude/events/rewards-hub/rewards-hub';
import { setAmplitudeGlobalLoyaltyLevel } from '@/tracking/amplitude/plugins/casino-event-context-enrichment-plugin';
import { trackPromoOfferPageViewedToAmplitude } from '@/tracking/amplitude/events/rewards-hub/promo-offer';
import { PageType } from '@/hooks/use-rewards/constants';
import { useMdsInstanceForRewardsHub } from '@/hooks/use-mds-instance';
import { useStore } from '@/store/store';
import { retrieve, remove, STORAGE_KEYS } from '@/utils/storage';
import { usePromotionLauncher } from '@/hooks/use-promotion-launcher/use-promotion-launcher.hook';

type CppEventsHandlerEvent = {
    type?: string;
    href?: string;
    originalHref?: string;
    uri?: string;
    promoCode?: string;
    label?: string;
    description?: string;
    promoName?: string;
    message?: string;
};

/**
 * Promo-scoped GameZone wrapper.
 */
const PromoGameZone = (props: ComponentProps<typeof GameZone>) => {
    useRenderTiming(PROMO_DETAILS_TIMINGS.GAMEZONE_RENDERED, props.games.length > 0);
    return <GameZone {...props} />;
};

type RewardsDetailsClientProps = {
    promoCode: string;
    pageConfig: RewardsPageConfig;
    isLoggedIn: boolean;
};

const RewardsDetailsClient: React.FC<RewardsDetailsClientProps> = ({ promoCode, pageConfig, isLoggedIn }) => {
    const sharedState = useRef<RewardsHubSharedState | null>(null);
    const pageViewedFired = useRef(false);
    const pendingPromoContext = useRef<{ promoId: string; promoName?: string } | null>(null);
    const navigationSource = useRef<string | null>(null);
    const rewardsConfig = useRewardsHubConfig(pageConfig, isLoggedIn);
    const [promoConfig] = useCppConfig();
    const routeConfig = useCppRoutes();
    const [eventsHandler, anchorCallback] = useCppEventsHandler(`${REFERRER_TYPE_REWARDS}/${promoCode}`);
    const eventsHandlerRef = useRef(eventsHandler);
    const wrappedApplication = isIosWrapper();
    const promotionLauncher = usePromotionLauncher();
    const { onPrimaryLinkClicked, onSecondaryLinkClicked, onCardClicked, onTermsExpanded, sendEventRef } =
        useRewardsPromoTracking({
            sharedStateRef: sharedState,
            pageType: PageType.OFFER
        });
    const userId = useStore((state) => state.userContext.accountSettings.accountID);
    const mdsInstance = useMdsInstanceForRewardsHub(userId);
    const [isPromoContentReady, setIsPromoContentReady] = useState(false);

    // fires once the promo content has painted after its data arrived.
    useRenderTiming(PROMO_DETAILS_TIMINGS.CONTENT_RENDERED, isPromoContentReady);

    useEffect(() => {
        eventsHandlerRef.current = eventsHandler;
    }, [eventsHandler]);

    useEffect(() => {
        const stored = retrieve(STORAGE_KEYS.NAVIGATION_SOURCE, 'session');
        if (stored) {
            navigationSource.current = stored;
            remove(STORAGE_KEYS.NAVIGATION_SOURCE, 'session');
        }
    }, []);

    const handlers = useMemo(
        () => ({
            handleSharedState: (state: RewardsHubSharedState) => {
                sharedState.current = state;
                setAmplitudeGlobalLoyaltyLevel(state.rewardsLoyaltyLevel);
                if (pendingPromoContext.current && !pageViewedFired.current) {
                    pageViewedFired.current = true;
                    trackPromoOfferPageViewedToAmplitude({
                        sendEvent: sendEventRef.current,
                        sharedState: sharedState.current,
                        context: pendingPromoContext.current,
                        source: navigationSource.current ?? undefined
                    });
                }
            },
            onPromoLoaded: ({ promoId, promoName }: { promoId: string; promoName?: string }) => {
                ddRumTimingOnce(PROMO_DETAILS_TIMINGS.DATA_READY);
                setIsPromoContentReady(true);
                if (pageViewedFired.current) return;
                pendingPromoContext.current = { promoId, promoName };
                if (sharedState.current !== null) {
                    pageViewedFired.current = true;
                    trackPromoOfferPageViewedToAmplitude({
                        sendEvent: sendEventRef.current,
                        sharedState: sharedState.current,
                        context: { promoId, promoName },
                        source: navigationSource.current ?? undefined
                    });
                }
            }
        }),
        [sendEventRef]
    );

    const promoEventsBridge = useMemo(
        () => (event: unknown) => {
            trackRewardsHubPromoEventToAmplitude({
                sendEvent: sendEventRef.current,
                event: event as RewardsHubPromoEvent,
                sharedState: sharedState.current,
                module: REWARDS_MODULE.PROMO_HUB_OFFER
            });
            eventsHandlerRef.current?.(event as CppEventsHandlerEvent);
        },
        [sendEventRef]
    );

    const promoHandlers = useMemo(
        () => ({
            ...promoConfig,
            isLoggedIn: rewardsConfig.isLoggedIn,
            routeConfig,
            wrappedApplication,
            onPrimaryLinkClicked,
            onSecondaryLinkClicked,
            onCardClicked,
            events: promoEventsBridge,
            anchorCallback,
            launcher: promotionLauncher,
            onTermsExpanded
        }),
        [
            promoConfig,
            rewardsConfig.isLoggedIn,
            routeConfig,
            wrappedApplication,
            onPrimaryLinkClicked,
            onSecondaryLinkClicked,
            onCardClicked,
            onTermsExpanded,
            promoEventsBridge,
            anchorCallback,
            promotionLauncher
        ]
    );

    return (
        <RewardsAppProvider>
            <PokerBuildPlaytechGate>
                <PromotionDetailsPage
                    config={rewardsConfig}
                    promoCode={promoCode}
                    gameZoneComponent={PromoGameZone}
                    features={rewardsConfig.features}
                    promoHandlers={promoHandlers}
                    MDSInstance={mdsInstance}
                    handlers={handlers}
                />
            </PokerBuildPlaytechGate>
        </RewardsAppProvider>
    );
};

export default RewardsDetailsClient;
